import React, { useState, useEffect, useMemo } from "react";
import axios from "../../services/axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { IoShareSocialOutline } from "react-icons/io5";
import downloadjs from "downloadjs";
import html2canvas from "html2canvas";

import { convertLanguageToField } from "../../utils/functions";

import Header, {
  NoHeaderScroll,
  BackHeaderLeft,
} from "../../components/navigation/header";
import QuizHeart from "../../components/quiz/heart";

import styles from "./quizGame.module.css";

// Animations
import { ReactComponent as woman1Answer } from "../../assets/animations/quiz/woman/mulher_1_resposta.svg";
import { ReactComponent as woman4Answer } from "../../assets/animations/quiz/woman/mulher_4_resposta.svg";
import { ReactComponent as woman1 } from "../../assets/animations/quiz/woman/mulher_1.svg";
import { ReactComponent as man1 } from "../../assets/animations/quiz/man/homem_1.svg";
import { ReactComponent as man1Answer } from "../../assets/animations/quiz/man/homem_1_resposta.svg";
import { ReactComponent as man3Answer } from "../../assets/animations/quiz/man/homem_3_resposta.svg";
import { ReactComponent as woman4 } from "../../assets/animations/quiz/woman/mulher_4.svg";
import { ReactComponent as woman2Answer } from "../../assets/animations/quiz/woman/mulher_2_resposta.svg";
import { ReactComponent as woman2 } from "../../assets/animations/quiz/woman/mulher_2.svg";
import { ReactComponent as woman3Answer } from "../../assets/animations/quiz/woman/mulher_3_resposta.svg";
import { ReactComponent as woman3 } from "../../assets/animations/quiz/woman/mulher_3.svg";
import { ReactComponent as man3 } from "../../assets/animations/quiz/man/homem_3.svg";
import { ReactComponent as man2Answer } from "../../assets/animations/quiz/man/homem_2_resposta.svg";
import { ReactComponent as man2 } from "../../assets/animations/quiz/man/homem_2.svg";

const markdowns = {
  woman: {
    0: { answer: woman1Answer, idle: woman1 },
    25: { answer: woman2Answer, idle: woman2 },
    50: { answer: woman3Answer, idle: woman3 },
    75: { answer: woman4Answer, idle: woman4 },
  },
  man: {
    0: { answer: man1Answer, idle: man1 },
    33: { answer: man2Answer, idle: man2 },
    66: { answer: man3Answer, idle: man3 },
  },
};

function Item({ text, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn ${
        selected ? styles.selected : ""
      } border-[#DF5076] btn-outline-brand w-full p-2 mb-4`}
    >
      <span className={`${selected ? "text-white" : ""}`}>{text}</span>
    </button>
  );
}

function ResultScreen({ points }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const levels = [t("resQuiz1"), t("resQuiz2"), t("resQuiz3"), t("resQuiz4")];

  const getCurrentLevel = () => {
    const pointsArray = Object.keys(levels)
      .map(Number)
      .sort((a, b) => a - b);

    for (let i = pointsArray.length - 1; i >= 0; i--) {
      if (points >= pointsArray[i]) {
        return levels[pointsArray[i]];
      }
    }

    return levels[0];
  };

  const handleAgain = () => {
    navigate("/quiz");
  };

  const handleShare = async () => {
    const game = document.getElementById("game-screen");
    const gameClone = game.cloneNode(true);

    const bodyStyle = getComputedStyle(document.body);

    game.querySelectorAll("#player-animation").forEach((svg) => {
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = document.createElement("img");
      img.src = "data:image/svg+xml;base64," + btoa(svgData);
      const style = getComputedStyle(svg);

      [...style].forEach((key) => {
        img.style[key] = style.getPropertyValue(key);
      });

      console.log(img.style.width, img.style.height);

      gameClone.querySelector("#player-animation").replaceWith(img);
    });

    [...bodyStyle].forEach((key) => {
      gameClone.style[key] = bodyStyle.getPropertyValue(key);
    });

    document.body.appendChild(gameClone);

    html2canvas(gameClone, {
      ignoreElements: (element) => {
        if (element.tagName === "SCRIPT") {
          return true;
        }

        if (element.classList.contains("no-print")) {
          return true;
        }

        return false;
      },
      height: gameClone.scrollHeight - 72,
      scrollY: -gameClone.scrollHeight,
    }).then((canvas) => {
      document.body.removeChild(gameClone);
      downloadjs(canvas.toDataURL("image/png"), "quiz.png", "image/png");
    });
  };

  return (
    <div className="flex flex-col items-center text-center ps-8 pe-8">
      <h2 className="font-bold text-3xl mb-5">{t("nivelRomantismo")}</h2>
      <div className={styles.progressResult}>
        <div className={styles.bar} style={{ width: `${points}%` }}></div>
      </div>
      <h3 className="font-bold text-3xl mt-3">{points}%:</h3>
      <h3 className="font-bold text-2xl">{getCurrentLevel()}</h3>
      <p className="font-semibold text-lg leading-5 mt-3 no-print">
        {t("shareQuizButton")}
      </p>
      <div className="flex mt-5">
        <button
          type="button"
          onClick={handleShare}
          className={`${styles.buttonShare} touchable-opacity no-print`}
        >
          <IoShareSocialOutline size={24} className="me-2" />
          {t("share")}
        </button>
        <button
          type="button"
          onClick={handleAgain}
          className={`${styles.buttonAgain} touchable-opacity no-print`}
        >
          {t("tryAgain")}
        </button>
      </div>
    </div>
  );
}

export default function QuizGame() {
  const { t, i18n } = useTranslation();
  const { player } = useLocation().state;
  const [selected, setSelected] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [options, setOptions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const { player1, player2 } = useSelector((state) => state.players);
  const [points, setPoints] = useState(0);
  const [animation, setAnimation] = useState(null);
  const [animationType, setAnimationType] = useState("idle");
  const [gameState, setGameState] = useState(0);
  const [hearts, setHearts] = useState([]);

  const p = player === 1 ? player1 : player2;

  const questionField = convertLanguageToField(i18n.language, "pergunta");
  const optionField = convertLanguageToField(i18n.language, "alternativa");

  useEffect(() => {
    axios
      .post("/quiz/getQuestions", {
        orientation: p.orientationCalc,
      })
      .then((response) => {
        const { data } = response;
        setQuestions(data);
        getOptions(data[0].id_pergunta);
      });
  }, []);

  useEffect(() => {
    const getAnimation = () => {
      if (p) {
        let type = p.gender === 1 ? "woman" : "man";
        const typeMarkdowns = markdowns[type];

        const closestKey = Object.keys(typeMarkdowns)
          .map(Number)
          .filter((key) => key <= points)
          .sort((a, b) => b - a)[0];

        let value =
          closestKey !== undefined ? typeMarkdowns[closestKey] : undefined;

        if (value) {
          setAnimation(value);
        }
      }
    };
    getAnimation();
  }, [points]);

  const getOptions = (id_pergunta) => {
    axios
      .post("/quiz/getOptions", {
        question: id_pergunta,
      })
      .then((response) => {
        const { data } = response;
        setOptions(data);
      });
  };

  const handleNext = () => {
    setPoints(points + questions[currentQuestion].peso);
    setAnimationType("answer");
    setSelected(null);
    setOptions([]);
    if (currentQuestion + 1 < questions.length) {
      getOptions(questions[currentQuestion + 1].id_pergunta);
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setGameState(1);
      prepareAnimation();
      setTimeout(() => {
        setGameState(2);
        setHearts([]);
      }, 6000);
    }
  };

  const prepareAnimation = () => {
    const heartsArray = [];
    for (let i = 0; i < 500; i++) {
      heartsArray.push(<QuizHeart key={`heart-${i}`} delay={i * 10 + 1000} />);
    }

    setHearts(heartsArray);
    setTimeout(() => {
      document.getElementById("game-screen").classList.add("opacity-0");
      setTimeout(() => {
        document.getElementById("game-screen").classList.remove("opacity-0");
      }, 1000);
    }, 5000);
  };

  const SvgAnimation = useMemo(() => {
    return animation
      ? animation[animationType]
      : () => {
          return null;
        };
  }, [animation, animationType]);

  return (
    <div
      id="game-screen"
      className={`container no-scroll mx-auto transition-all duration-1000`}
    >
      <Header HeaderLeft={BackHeaderLeft} HeaderCenter={() => {}} />
      <NoHeaderScroll className="flex flex-col">
        {gameState === 1 ? <div className="-z-[2]">{hearts}</div> : null}
        <div>
          <SvgAnimation
            id="player-animation"
            className={`${styles.animation} ${
              gameState === 1 ? styles.transition : ""
            }
              ${gameState === 2 ? `${styles.end}` : ""}
            `}
          />
        </div>
        {gameState === 1 ? <div>{hearts}</div> : null}
        {gameState === 1 ? <div></div> : null}
        {gameState === 2 ? <ResultScreen points={points} /> : null}
        {gameState === 0 ? (
          <div className="flex flex-col flex-1">
            <div className="flex flex-1 h-[30%]">
              <div className="mb-4">
                <div className={styles.progress}>
                  <div
                    className={styles.bar}
                    style={{ height: `${points}%` }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="bg-white h-[70%] flex flex-col rounded-t-2xl pt-3 pe-4 ps-4">
              <h2 className="text-[#DF515F] font-bold text-2xl">
                {t("pergunta")}{" "}
                <span className="font-medium">
                  {currentQuestion + 1}/{questions.length}
                </span>
              </h2>
              <p className="text-[#454545] font-semibold text-lg leading-6 mt-3">
                {questions?.length
                  ? questions[currentQuestion][questionField]
                  : ""}
              </p>
              <div className="flex-1 scroll mt-3">
                {options.map((option, index) => {
                  return (
                    <Item
                      key={`${index}`}
                      text={option[optionField]}
                      selected={selected === index}
                      onClick={() => setSelected(index)}
                    />
                  );
                })}
              </div>
              <div>
                <button
                  type="button"
                  disabled={selected === null}
                  onClick={handleNext}
                  className="btn bg-[#DF5076] disabled:bg-[#DF5076aa] text-white w-full rounded-full p-2 mt-3 mb-3 text-xl"
                >
                  {t("next")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </NoHeaderScroll>
    </div>
  );
}
